/**
 * Nova proposta (`/nova`) — o coração do produto: transformar um pedido de
 * equipamentos num documento correto em menos de dois minutos.
 *
 * Duas colunas: o formulário de entrada à esquerda, o painel de leitura fixo
 * (sticky) à direita — o momento de maior ousadia visual do sistema (ver
 * docs/DESIGN.md). O resumo é calculado localmente com `calcPropostaLocal`
 * (o mesmo motor de `shared/tax.js` que o servidor roda) para reagir sem
 * round-trip; o servidor recalcula tudo de novo ao salvar e é sempre a fonte
 * de verdade final.
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ApiError, get } from '@/lib/api';
import { fmtBRL, fmtDate, fmtPct, isValidDocumento, maskDocumento, maskPhone, tipoDocumento } from '@/lib/format';
import type { Client, Paged, Product, Settings, StateTax, TipoCalculo } from '@/lib/types';
import { calcPropostaLocal, useCreateProposal, type TaxItemInput } from '@/hooks/usePropostas';
import { useToast } from '@/hooks/useToast';
import { PageHeader } from '@/components/PageHeader';
import { Panel } from '@/components/Panel';
import { Field } from '@/components/Field';
import { Button } from '@/components/Button';
import { Money } from '@/components/Money';
import { Percent } from '@/components/Percent';
import { Badge } from '@/components/Badge';
import './NovaProposta.css';

interface ClientFormState {
  id?: number;
  razaoSocial: string;
  cnpj: string;
  contato: string;
  celular: string;
  email: string;
}

const EMPTY_CLIENT: ClientFormState = {
  razaoSocial: '',
  cnpj: '',
  contato: '',
  celular: '',
  email: '',
};

/** Junta uma lista em português: "a, b e c". */
function joinPtBr(items: string[]): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  return `${items.slice(0, -1).join(', ')} e ${items[items.length - 1]}`;
}

function cents(value: number): number {
  return Math.round(value * 100);
}

function productDisabledReason(product: Product): string | null {
  if (product.currentPrice) return null;
  if (product.nextPrice?.validFrom) {
    return `Sem preço vigente — o próximo começa em ${fmtDate(product.nextPrice.validFrom)}.`;
  }
  return 'Sem preço vigente — não pode ser incluído nesta proposta.';
}

export function NovaProposta() {
  const navigate = useNavigate();
  const toast = useToast();
  const createProposal = useCreateProposal();

  // ------------------------------------------------------------------ dados --

  const productsQuery = useQuery({
    queryKey: ['products', 'active-for-proposal'],
    queryFn: () => get<{ data: Product[] }>('/products?active=1').then((r) => r.data),
  });
  const products = useMemo(() => productsQuery.data ?? [], [productsQuery.data]);

  const statesQuery = useQuery({
    queryKey: ['states'],
    queryFn: () => get<{ data: StateTax[] }>('/states').then((r) => r.data),
    staleTime: 5 * 60_000,
  });
  const states = useMemo(() => statesQuery.data ?? [], [statesQuery.data]);

  const settingsQuery = useQuery({
    queryKey: ['settings'],
    queryFn: () => get<Settings>('/settings'),
    staleTime: 5 * 60_000,
  });

  // -------------------------------------------------------------- formulário --

  const [client, setClient] = useState<ClientFormState>(EMPTY_CLIENT);
  const [saveClient, setSaveClient] = useState(true);
  const [clientQuery, setClientQuery] = useState('');
  const [debouncedClientQuery, setDebouncedClientQuery] = useState('');

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedClientQuery(clientQuery.trim()), 300);
    return () => window.clearTimeout(t);
  }, [clientQuery]);

  const clientSearchQuery = useQuery({
    queryKey: ['client-search', debouncedClientQuery],
    queryFn: () => get<Paged<Client>>(`/clients?perPage=6&q=${encodeURIComponent(debouncedClientQuery)}`),
    enabled: debouncedClientQuery.length >= 2,
  });

  function pickClient(c: Client) {
    setClient({
      id: c.id,
      razaoSocial: c.razaoSocial,
      cnpj: c.cnpj,
      contato: c.contato ?? '',
      celular: c.celular ?? '',
      email: c.email ?? '',
    });
    setClientQuery('');
  }

  function unlinkClient() {
    setClient((c) => ({ ...c, id: undefined }));
  }

  const [estadoUf, setEstadoUf] = useState('');
  const [tipoCalculo, setTipoCalculo] = useState<TipoCalculo>('dentro');
  const [selection, setSelection] = useState<Record<number, number>>({});
  const [validadeDias, setValidadeDias] = useState<number | ''>('');
  const [notes, setNotes] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Preenche a validade padrão assim que os ajustes chegarem, só se a pessoa ainda não mexeu.
  useEffect(() => {
    if (settingsQuery.data && validadeDias === '') {
      setValidadeDias(settingsQuery.data.proposalDefaults.validadeDias);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsQuery.data]);

  function toggleProduct(product: Product, checked: boolean) {
    setSelection((current) => {
      const next = { ...current };
      if (checked) next[product.id] = current[product.id] ?? 1;
      else delete next[product.id];
      return next;
    });
  }

  function setQty(productId: number, qty: number) {
    const clamped = Math.max(1, Math.min(999, Math.round(qty) || 1));
    setSelection((current) => (current[productId] === undefined ? current : { ...current, [productId]: clamped }));
  }

  const estado = useMemo(() => states.find((s) => s.uf === estadoUf) ?? null, [states, estadoUf]);

  const selectedItems = useMemo<TaxItemInput[]>(() => {
    return products
      .filter((p) => selection[p.id] !== undefined && p.currentPrice)
      .map((p) => ({
        productId: p.id,
        qty: selection[p.id],
        unitValue: p.currentPrice ? p.currentPrice.unitValue : 0,
        ipi: p.currentPrice ? p.currentPrice.ipi : 0,
      }));
  }, [products, selection]);

  const totalQty = useMemo(() => selectedItems.reduce((sum, it) => sum + it.qty, 0), [selectedItems]);

  const calc = useMemo(
    () =>
      calcPropostaLocal(
        selectedItems,
        estado ? { alInter: estado.alInter, alDest: estado.alDest, fcp: estado.fcp } : null,
        tipoCalculo
      ),
    [selectedItems, estado, tipoCalculo]
  );

  // Só acusa erro quando o documento já tem tamanho de CPF (11) ou CNPJ (14).
  const docLooksInvalid = tipoDocumento(client.cnpj) !== null && !isValidDocumento(client.cnpj);

  const missing = useMemo(() => {
    const list: string[] = [];
    if (!client.razaoSocial.trim()) list.push('razão social do cliente');
    if (!isValidDocumento(client.cnpj)) list.push('CNPJ/CPF do cliente');
    if (!estadoUf) list.push('estado de destino');
    if (totalQty === 0) list.push('ao menos um equipamento');
    return list;
  }, [client.razaoSocial, client.cnpj, estadoUf, totalQty]);

  const canGenerate = missing.length === 0 && !submitting;

  // --------------------------------------------------------------- envio --

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!canGenerate) return;
    setSubmitting(true);
    setFormError(null);
    setFieldErrors({});
    const displayedTotal = calc.totalGeral;
    try {
      const proposal = await createProposal.mutateAsync({
        client: {
          id: client.id,
          razaoSocial: client.razaoSocial.trim(),
          cnpj: client.cnpj,
          contato: client.contato || undefined,
          celular: client.celular || undefined,
          email: client.email || undefined,
        },
        saveClient,
        estadoUf,
        tipoCalculo,
        validadeDias: typeof validadeDias === 'number' ? validadeDias : undefined,
        notes: notes || undefined,
        items: selectedItems.map((it) => ({ productId: it.productId, qty: it.qty })),
      });

      if (Math.abs(proposal.totalGeral - Math.round(displayedTotal * 100) / 100) > 0.01) {
        toast.info(
          `Proposta ${proposal.code} gerada — mas os preços mudaram entre abrir esta tela e gerar. ` +
            `O valor salvo (a fonte de verdade) é ${fmtBRL(proposal.totalGeral)}, não os ${fmtBRL(displayedTotal)} que o painel mostrava.`,
          { duration: 9000 }
        );
      } else {
        toast.success(`Proposta ${proposal.code} gerada — total ${fmtBRL(proposal.totalGeral)}.`);
      }
      navigate(`/propostas/${proposal.id}`);
    } catch (err) {
      if (err instanceof ApiError) {
        setFormError(err.message);
        setFieldErrors(err.fields ?? {});
        toast.error(err.message);
      } else {
        const msg = 'Não foi possível gerar a proposta agora. Tente de novo em instantes.';
        setFormError(msg);
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  }

  // ------------------------------------------------------------------ leitura --

  const fracBase = calc.totalGeral > 0 ? calc.subtotal / calc.totalGeral : 0;
  const fracDifal = calc.totalGeral > 0 ? calc.totalDifal / calc.totalGeral : 0;
  const fracFcp = calc.totalGeral > 0 ? calc.totalFCP / calc.totalGeral : 0;

  const readout = (
    <>
      <p className="readout__eyebrow">Total geral</p>
      <Money
        key={cents(calc.totalGeral)}
        value={calc.totalGeral}
        size="lg"
        className="readout__total-value nova-value-pulse"
      />

      <div
        className="stacked-bar readout__bar"
        role="img"
        aria-label={`Produtos e IPI ${(fracBase * 100).toFixed(0)}%, DIFAL ${(fracDifal * 100).toFixed(0)}%, FCP ${(fracFcp * 100).toFixed(0)}%`}
      >
        <span className="stacked-bar__seg stacked-bar__seg--base" style={{ width: `${fracBase * 100}%` }} />
        <span className="stacked-bar__seg stacked-bar__seg--difal" style={{ width: `${fracDifal * 100}%` }} />
        <span className="stacked-bar__seg stacked-bar__seg--fcp" style={{ width: `${fracFcp * 100}%` }} />
      </div>

      <ul className="readout__legend">
        <li className="readout__legend-item">
          <span className="readout__swatch stacked-bar__seg--base" aria-hidden="true" />
          produtos + IPI <Percent value={fracBase} />
        </li>
        <li className="readout__legend-item">
          <span className="readout__swatch stacked-bar__seg--difal" aria-hidden="true" />
          DIFAL <Percent value={fracDifal} />
        </li>
        <li className="readout__legend-item">
          <span className="readout__swatch stacked-bar__seg--fcp" aria-hidden="true" />
          FCP <Percent value={fracFcp} />
        </li>
      </ul>

      <dl className="readout__rows">
        <div className="readout__row">
          <dt>Nº de equipamentos</dt>
          <dd key={totalQty} className="mono value-fade nova-value-pulse">
            {totalQty}
          </dd>
        </div>
        <div className="readout__row">
          <dt>Subtotal produtos + IPI</dt>
          <dd>
            <Money key={cents(calc.subtotal)} value={calc.subtotal} className="value-fade nova-value-pulse" />
          </dd>
        </div>
        <div className="readout__row">
          <dt>
            ICMS DIFAL{estado ? <Percent value={estado.alDest - estado.alInter} className="readout__row-rate" /> : null}
          </dt>
          <dd>
            <Money key={cents(calc.totalDifal)} value={calc.totalDifal} className="value-fade nova-value-pulse" />
          </dd>
        </div>
        <div className="readout__row">
          <dt>
            FCP{estado && estado.fcp > 0 ? <Percent value={estado.fcp} className="readout__row-rate" /> : null}
          </dt>
          <dd>
            <Money key={cents(calc.totalFCP)} value={calc.totalFCP} className="value-fade nova-value-pulse" />
          </dd>
        </div>
      </dl>

      {missing.length > 0 ? (
        <p className="readout__missing">Falta: {joinPtBr(missing)}.</p>
      ) : (
        <p className="readout__missing readout__missing--ready">Tudo certo — pode gerar a proposta.</p>
      )}
    </>
  );

  return (
    <>
      <PageHeader
        title="Nova proposta"
        meta="Preencha os dados, marque os equipamentos e gere o documento pronto para impressão."
      />

      <form className="nova-page" onSubmit={handleSubmit} noValidate>
        <div className="nova-grid">
          <div className="nova-form stack-lg">
            {formError ? (
              <p className="form-alert form-alert-error" role="alert">
                {formError}
              </p>
            ) : null}

            <Panel title="Cliente">
              <div className="stack">
                <div className="client-search">
                  <Field
                    label="Buscar cliente cadastrado"
                    placeholder="Razão social, CNPJ ou CPF…"
                    value={clientQuery}
                    onChange={(e) => setClientQuery(e.target.value)}
                    hint="Preenche o bloco abaixo com um cliente já cadastrado."
                  />
                  {debouncedClientQuery.length >= 2 ? (
                    <div className="client-search__results">
                      {clientSearchQuery.isLoading ? (
                        <p className="client-search__empty">Buscando…</p>
                      ) : (clientSearchQuery.data?.data.length ?? 0) === 0 ? (
                        <p className="client-search__empty">Nenhum cliente encontrado.</p>
                      ) : (
                        clientSearchQuery.data?.data.map((c) => (
                          <button
                            key={c.id}
                            type="button"
                            className="client-search__item"
                            onClick={() => pickClient(c)}
                          >
                            <span>{c.razaoSocial}</span>
                            <span className="mono faint">{c.cnpj}</span>
                          </button>
                        ))
                      )}
                    </div>
                  ) : null}
                </div>

                {client.id ? (
                  <p className="client-linked">
                    Vinculado ao cadastro existente.
                    <button type="button" onClick={unlinkClient}>
                      Usar como cliente novo
                    </button>
                  </p>
                ) : null}

                <div className="field-grid">
                  <Field
                    label="Razão social"
                    required
                    value={client.razaoSocial}
                    onChange={(e) => setClient((c) => ({ ...c, razaoSocial: e.target.value }))}
                    error={fieldErrors['client.razaoSocial']}
                  />
                  <Field
                    label="CNPJ / CPF"
                    required
                    inputMode="numeric"
                    placeholder="00.000.000/0000-00 ou 000.000.000-00"
                    value={client.cnpj}
                    onChange={(e) =>
                      setClient((c) => ({ ...c, id: undefined, cnpj: maskDocumento(e.target.value) }))
                    }
                    error={fieldErrors['client.cnpj'] ?? (docLooksInvalid ? 'CNPJ/CPF inválido.' : undefined)}
                  />
                </div>
                <div className="nova-row-3">
                  <Field
                    label="Contato"
                    placeholder="Nome do responsável"
                    value={client.contato}
                    onChange={(e) => setClient((c) => ({ ...c, contato: e.target.value }))}
                  />
                  <Field
                    label="Celular / WhatsApp"
                    inputMode="numeric"
                    placeholder="(00) 00000-0000"
                    value={client.celular}
                    onChange={(e) => setClient((c) => ({ ...c, celular: maskPhone(e.target.value) }))}
                  />
                  <Field
                    label="E-mail"
                    type="email"
                    value={client.email}
                    onChange={(e) => setClient((c) => ({ ...c, email: e.target.value }))}
                  />
                </div>
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={saveClient}
                    onChange={(e) => setSaveClient(e.target.checked)}
                  />
                  Salvar este cliente no cadastro
                </label>
              </div>
            </Panel>

            <Panel title="Destino">
              <div className="stack">
                <Field
                  as="select"
                  label="Estado de destino"
                  required
                  value={estadoUf}
                  onChange={(e) => setEstadoUf(e.target.value)}
                  error={fieldErrors.estadoUf}
                >
                  <option value="">Selecione…</option>
                  {states.map((s) => (
                    <option key={s.uf} value={s.uf}>
                      {s.nome}
                    </option>
                  ))}
                </Field>

                {estado ? (
                  <div className="tax-reading">
                    <span>
                      interestadual <strong>{fmtPct(estado.alInter)}</strong>
                    </span>
                    <span>
                      destino <strong>{fmtPct(estado.alDest)}</strong>
                    </span>
                    {estado.fcp > 0 ? (
                      <span>
                        FCP <strong>{fmtPct(estado.fcp)}</strong>
                      </span>
                    ) : null}
                    {estado.st ? <Badge variant="attention">sujeito a ST</Badge> : null}
                  </div>
                ) : (
                  <p className="muted">Selecione um estado para ver as alíquotas.</p>
                )}

                <div className="calc-type-row" role="radiogroup" aria-label="Tipo de cálculo">
                  <label className={`calc-type-option${tipoCalculo === 'dentro' ? ' is-selected' : ''}`}>
                    <input
                      type="radio"
                      name="tipoCalculo"
                      value="dentro"
                      checked={tipoCalculo === 'dentro'}
                      onChange={() => setTipoCalculo('dentro')}
                      style={{ accentColor: 'var(--accent)' }}
                    />
                    <span>
                      <span className="calc-type-option__title">Por dentro</span>
                      <span className="calc-type-option__desc">
                        Base majorada pela alíquota interna do destino — método mais usado.
                      </span>
                    </span>
                  </label>
                  <label className={`calc-type-option${tipoCalculo === 'fora' ? ' is-selected' : ''}`}>
                    <input
                      type="radio"
                      name="tipoCalculo"
                      value="fora"
                      checked={tipoCalculo === 'fora'}
                      onChange={() => setTipoCalculo('fora')}
                      style={{ accentColor: 'var(--accent)' }}
                    />
                    <span>
                      <span className="calc-type-option__title">Por fora</span>
                      <span className="calc-type-option__desc">
                        DIFAL calculado direto sobre a diferença de alíquotas.
                      </span>
                    </span>
                  </label>
                </div>
              </div>
            </Panel>

            <Panel title="Equipamentos">
              {productsQuery.isLoading ? (
                <p className="muted">Carregando catálogo…</p>
              ) : products.length === 0 ? (
                <p className="muted">Nenhum equipamento ativo no catálogo ainda.</p>
              ) : (
                <div className="table-wrap">
                  <table className="table equip-table">
                    <caption className="sr-only">Equipamentos disponíveis para a proposta</caption>
                    <thead>
                      <tr>
                        <th className="sr-only">Selecionar</th>
                        <th>Equipamento</th>
                        <th className="num">Vl. unitário</th>
                        <th className="num">Qtd.</th>
                        <th className="num">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.map((p) => {
                        const reason = productDisabledReason(p);
                        const disabled = !!reason;
                        const selected = selection[p.id] !== undefined;
                        const qty = selection[p.id] ?? 1;
                        const lineTotal =
                          selected && p.currentPrice
                            ? p.currentPrice.unitValue * qty * (1 + p.currentPrice.ipi)
                            : null;
                        const inputId = `equip-${p.id}`;
                        return (
                          <tr key={p.id} className={disabled ? 'equip-row--disabled' : undefined}>
                            <td>
                              <input
                                id={inputId}
                                type="checkbox"
                                checked={selected}
                                disabled={disabled}
                                style={{ accentColor: 'var(--accent)' }}
                                onChange={(e) => toggleProduct(p, e.target.checked)}
                              />
                            </td>
                            <td>
                              <label htmlFor={inputId} className="equip-table__name">
                                {p.name}
                              </label>
                              {p.details ? <p className="equip-table__details">{p.details}</p> : null}
                              {reason ? <p className="equip-table__reason">{reason}</p> : null}
                            </td>
                            <td className="num">{p.currentPrice ? <Money value={p.currentPrice.unitValue} /> : '—'}</td>
                            <td className="num">
                              <input
                                type="number"
                                className="input mono equip-qty-input"
                                min={1}
                                max={999}
                                value={qty}
                                disabled={disabled || !selected}
                                aria-label={`Quantidade de ${p.name}`}
                                onChange={(e) => setQty(p.id, Number(e.target.value))}
                              />
                            </td>
                            <td className="num">{lineTotal !== null ? <Money value={lineTotal} /> : '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>

            <Panel title="Validade e observações">
              <div className="nova-row-validade">
                <Field
                  label="Validade (dias)"
                  type="number"
                  min={1}
                  value={validadeDias}
                  onChange={(e) => setValidadeDias(e.target.value === '' ? '' : Number(e.target.value))}
                />
                <Field
                  as="textarea"
                  label="Observações internas"
                  hint="Uso interno da equipe — não aparece no documento gerado."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
            </Panel>
          </div>

          <aside className="nova-aside no-print">
            <Panel
              title="Leitura"
              className="readout"
              footer={
                <Button type="submit" disabled={!canGenerate} className="readout__generate">
                  {submitting ? 'Gerando…' : 'Gerar proposta'}
                </Button>
              }
            >
              {readout}
            </Panel>
          </aside>
        </div>

        <div className="nova-mobilebar no-print">
          <div>
            <p className="nova-mobilebar__label">Total geral</p>
            <Money key={cents(calc.totalGeral)} value={calc.totalGeral} size="lg" />
          </div>
          <Button type="submit" disabled={!canGenerate}>
            {submitting ? 'Gerando…' : 'Gerar proposta'}
          </Button>
        </div>
      </form>
    </>
  );
}
